import { CATEGORIES, US_STATES } from "@/lib/theme";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const inputCls = "mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute";
const labelCls = "font-display text-xs uppercase tracking-widest text-rig-dim";

export function ReceiptFields({ form, set, disabled = false }) {
  const isFuel = form.category === "Fuel";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label className={labelCls}>Vendor</Label>
        <Input
          value={form.vendor}
          onChange={(e) => set("vendor", e.target.value)}
          disabled={disabled}
          placeholder="Pilot, Loves, TA…"
          data-testid="field-vendor"
          className={inputCls}
        />
      </div>

      <div>
        <Label className={labelCls}>Amount (USD)</Label>
        <Input
          type="number"
          step="0.01"
          inputMode="decimal"
          value={form.amount}
          onChange={(e) => set("amount", e.target.value)}
          disabled={disabled}
          placeholder="0.00"
          data-testid="field-amount"
          className={inputCls}
        />
      </div>

      <div>
        <Label className={labelCls}>Date</Label>
        <Input
          type="date"
          value={form.receipt_date}
          onChange={(e) => set("receipt_date", e.target.value)}
          disabled={disabled}
          data-testid="field-date"
          className={inputCls}
        />
      </div>

      <div>
        <Label className={labelCls}>Category</Label>
        <Select value={form.category} onValueChange={(v) => set("category", v)} disabled={disabled}>
          <SelectTrigger data-testid="field-category" className={inputCls}>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent className="border-rig-border bg-rig-surface text-white">
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c} className="focus:bg-rig-surface2">
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className={labelCls}>Location (optional)</Label>
        <Input
          value={form.gps_label || ""}
          onChange={(e) => set("gps_label", e.target.value)}
          disabled={disabled}
          placeholder="Oklahoma City, OK"
          data-testid="field-location"
          className={inputCls}
        />
      </div>

      {isFuel && (
        <>
          <div>
            <Label className={labelCls}>Gallons (IFTA)</Label>
            <Input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={form.gallons ?? ""}
              onChange={(e) => set("gallons", e.target.value)}
              disabled={disabled}
              placeholder="e.g. 120.5"
              data-testid="field-gallons"
              className={inputCls}
            />
          </div>
          <div>
            <Label className={labelCls}>State (IFTA)</Label>
            <Select value={form.state || ""} onValueChange={(v) => set("state", v)} disabled={disabled}>
              <SelectTrigger data-testid="field-state" className={inputCls}>
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent className="max-h-64 border-rig-border bg-rig-surface text-white">
                {US_STATES.map((s) => (
                  <SelectItem key={s} value={s} className="focus:bg-rig-surface2">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <div className="sm:col-span-2">
        <Label className={labelCls}>Notes</Label>
        <Textarea
          value={form.notes || ""}
          onChange={(e) => set("notes", e.target.value)}
          disabled={disabled}
          placeholder="Anything worth remembering for the audit…"
          data-testid="field-notes"
          className="mt-1.5 min-h-[80px] border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
        />
      </div>
    </div>
  );
}

export const emptyReceipt = () => ({
  vendor: "",
  amount: "",
  receipt_date: new Date().toISOString().slice(0, 10),
  category: "Fuel",
  notes: "",
  gallons: "",
  state: "",
  gps_label: "",
  image_base64: "",
});
